import { useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config/api';

/**
 * Subscribe to the backend notification SSE stream.
 * EventSource auto-reconnects on error; we just (re)attach handlers.
 *
 * @param {Object} p
 * @param {boolean} p.enabled - only connect on protected routes
 * @param {(notif:Object)=>void} p.onNotification - fires on each `notification` event
 * @param {(meta:Object)=>void} p.onMeta - fires on the `meta` event (unread count)
 * @param {(data:Object)=>void} [p.onAlertsChanged] - fires on `alerts_changed`:
 *   the set of live fraud alerts / hot scams changed, or this user opened one
 *   (the same signal the desktop ribbon re-counts its badge on)
 */
export function useNotificationStream({ enabled, onNotification, onMeta, onAlertsChanged }) {
  const onNotifRef = useRef(onNotification);
  const onMetaRef = useRef(onMeta);
  const onAlertsRef = useRef(onAlertsChanged);
  onNotifRef.current = onNotification;
  onMetaRef.current = onMeta;
  onAlertsRef.current = onAlertsChanged;

  useEffect(() => {
    if (!enabled) return undefined;
    const es = new EventSource(`${API_BASE_URL}/bff/notifications/stream`, {
      withCredentials: true,
    });
    const handleNotif = (e) => {
      try { onNotifRef.current?.(JSON.parse(e.data)); } catch (err) { /* ignore */ }
    };
    const handleMeta = (e) => {
      try { onMetaRef.current?.(JSON.parse(e.data)); } catch (err) { /* ignore */ }
    };
    const handleAlerts = (e) => {
      try { onAlertsRef.current?.(JSON.parse(e.data)); } catch (err) { /* ignore */ }
    };
    es.addEventListener('notification', handleNotif);
    es.addEventListener('meta', handleMeta);
    es.addEventListener('alerts_changed', handleAlerts);
    es.onerror = () => { /* browser auto-reconnects with Last-Event-ID */ };
    return () => es.close();
  }, [enabled]);
}
